import { isInvoiceOverdue, type InvoiceFilterRow } from '@/pages/invoices/invoiceFilters';

export interface InvoiceKpiRow extends InvoiceFilterRow {
  total_paid_cents?: number | null;
  kind?: string | null;
}

export interface InvoicePaymentAmount {
  invoice_id: string;
  amount_cents: number;
  payment_date: string | null;
}

export interface InvoiceKpis {
  issuedCents: number;
  issuedCount: number;
  outstandingCents: number;
  outstandingCount: number;
  overdueCents: number;
  overdueCount: number;
  paidCents: number;
  paidCount: number;
}

function inSameMonth(iso: string | null | undefined, now: Date): boolean {
  if (!iso) return false;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return false;
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
}

function remainingCents(row: InvoiceKpiRow): number {
  return Math.max(0, row.total_cents - (row.total_paid_cents ?? 0));
}

function isOutstanding(row: InvoiceKpiRow, now: Date): boolean {
  if (row.status === 'partial' || row.status === 'overdue' || row.status === 'expired_pay_link') {
    return remainingCents(row) > 0;
  }
  if (row.status === 'issued') return remainingCents(row) > 0 || isInvoiceOverdue(row, now);
  return false;
}

export function computeInvoiceKpis(
  rows: readonly InvoiceKpiRow[],
  now: Date,
  payments?: readonly InvoicePaymentAmount[],
): InvoiceKpis {
  const kpis: InvoiceKpis = {
    issuedCents: 0,
    issuedCount: 0,
    outstandingCents: 0,
    outstandingCount: 0,
    overdueCents: 0,
    overdueCount: 0,
    paidCents: 0,
    paidCount: 0,
  };

  for (const row of rows) {
    if (row.kind === 'credit_note') continue;
    if (inSameMonth(row.issued_at, now)) {
      kpis.issuedCents += row.total_cents;
      kpis.issuedCount += 1;
    }
    if (isOutstanding(row, now)) {
      kpis.outstandingCents += remainingCents(row);
      kpis.outstandingCount += 1;
    }
    if (isInvoiceOverdue(row, now)) {
      kpis.overdueCents += remainingCents(row);
      kpis.overdueCount += 1;
    }
    if (
      !payments &&
      row.status === 'paid' &&
      inSameMonth(row.paid_at ?? row.last_payment_at, now)
    ) {
      kpis.paidCents += row.total_cents;
      kpis.paidCount += 1;
    }
  }

  if (payments) {
    const seen = new Set<string>();
    for (const payment of payments) {
      if (!inSameMonth(payment.payment_date, now) || payment.amount_cents === 0) continue;
      kpis.paidCents += payment.amount_cents;
      if (!seen.has(payment.invoice_id)) {
        seen.add(payment.invoice_id);
        kpis.paidCount += 1;
      }
    }
  }

  return kpis;
}
